import { zodResolver } from '@hookform/resolvers/zod'
import { CalendarRange } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { toast } from '@/components/feedback/toast-store'
import { Input } from '@/components/ui/Input'
import { useSaveYear } from '../api'
import type { AcademicYear } from '../types'
import { FormDialog } from './FormDialog'
import { ToggleField } from './ToggleField'

const yearSchema = z
  .object({
    name: z.string().trim().min(1, 'Enter a name, e.g. 2026–2027.').max(50, 'Use at most 50 characters.'),
    startsOn: z.string().min(1, 'Choose the first day of the year.'),
    endsOn: z.string().min(1, 'Choose the last day of the year.'),
    isCurrent: z.boolean(),
  })
  .refine((v) => !v.startsOn || !v.endsOn || v.endsOn > v.startsOn, {
    path: ['endsOn'],
    message: 'The end date must be after the start date.',
  })

type YearValues = z.infer<typeof yearSchema>
const FIELDS = ['name', 'startsOn', 'endsOn', 'isCurrent'] as const
const FORM_ID = 'academic-year-form'

interface YearDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Year to edit; omit to create a new one. */
  year?: AcademicYear | null
}

export function YearDialog({ open, onOpenChange, year }: YearDialogProps) {
  const { t } = useTranslation('academic')
  const save = useSaveYear()
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<CalendarRange />}
      title={year ? t('Edit academic year') : t('New academic year')}
      description={t('Enrollments, attendance and exams are recorded against an academic year.')}
      formId={FORM_ID}
      submitLabel={year ? t('Save changes') : t('Create year')}
      pending={save.isPending}
    >
      <YearForm
        year={year ?? null}
        onSubmit={async (values) => {
          await save.mutateAsync({ ...values, id: year?.id })
          toast.success(year ? t('Academic year updated') : t('Academic year created'), values.name)
          onOpenChange(false)
        }}
      />
    </FormDialog>
  )
}

function YearForm({
  year,
  onSubmit,
}: {
  year: AcademicYear | null
  onSubmit: (values: YearValues) => Promise<void>
}) {
  const { t } = useTranslation('academic')
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<YearValues>({
    resolver: zodResolver(yearSchema),
    defaultValues: {
      name: year?.name ?? '',
      startsOn: year?.startsOn ?? '',
      endsOn: year?.endsOn ?? '',
      isCurrent: year?.isCurrent ?? false,
    },
  })

  const submit = handleSubmit(async (values) => {
    try {
      await onSubmit(values)
    } catch (e) {
      applyServerErrors(e, setError, FIELDS)
    }
  })

  return (
    <form id={FORM_ID} onSubmit={(e) => void submit(e)} noValidate className="grid gap-4 sm:grid-cols-2">
      <FormField label={t('Name')} error={errors.name?.message} required className="sm:col-span-2">
        <Input placeholder="2026–2027" autoComplete="off" {...register('name')} />
      </FormField>
      <FormField label={t('Starts on')} error={errors.startsOn?.message} required>
        <Input type="date" {...register('startsOn')} />
      </FormField>
      <FormField label={t('Ends on')} error={errors.endsOn?.message} required>
        <Input type="date" {...register('endsOn')} />
      </FormField>
      <Controller
        control={control}
        name="isCurrent"
        render={({ field }) => (
          <ToggleField
            className="rounded-control border border-line bg-surface-muted/60 p-3 sm:col-span-2"
            label={t('Make this the current year')}
            description={
              year?.isCurrent
                ? t('This is already the current year. Set another year as current to change it.')
                : t(
                    'New admissions and the dashboard will use this year. The previous current year is switched off.',
                  )
            }
            checked={field.value}
            onCheckedChange={field.onChange}
            disabled={year?.isCurrent}
          />
        )}
      />
    </form>
  )
}
